// src/components/TempoControl.tsx
import { memo } from 'react';
import '../styles/TempoControl.css';

interface TempoControlProps {
  currentTempo: number;
  scoreTempo: number;
  onChange: (newTempo: number) => void;
  disabled?: boolean;
}

const MIN_TEMPO = 40;
const TEMPO_INCREMENT = 5;
const MAX_TEMPO_MULTIPLIER = 1.5;

export const TempoControl = memo(function TempoControl({
  currentTempo,
  scoreTempo,
  onChange,
  disabled = false,
}: TempoControlProps) {
  const maxTempo = Math.round(scoreTempo * MAX_TEMPO_MULTIPLIER);
  const isBelowOriginal = currentTempo < scoreTempo;

  const handleDecrease = () => {
    const newTempo = Math.max(MIN_TEMPO, currentTempo - TEMPO_INCREMENT);
    onChange(newTempo);
  };

  const handleIncrease = () => {
    const newTempo = Math.min(maxTempo, currentTempo + TEMPO_INCREMENT);
    onChange(newTempo);
  };

  const canDecrease = currentTempo > MIN_TEMPO;
  const canIncrease = currentTempo < maxTempo;

  return (
    <div className="tempo-control">
      <span className="tempo-label">Tempo:</span>
      <button
        data-testid="tempo-decrease"
        className="tempo-button"
        onClick={handleDecrease}
        disabled={disabled || !canDecrease}
        aria-label="Decrease tempo"
      >
        −
      </button>
      <span
        data-testid="tempo-value"
        className={`tempo-value ${isBelowOriginal ? 'below-original' : ''}`}
      >
        <span className="tempo-number">{currentTempo}</span>
        <span className="tempo-unit">BPM</span>
      </span>
      <button
        data-testid="tempo-increase"
        className="tempo-button"
        onClick={handleIncrease}
        disabled={disabled || !canIncrease}
        aria-label="Increase tempo"
      >
        +
      </button>
    </div>
  );
});
